import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { useToggleFeaturedArticle } from '~/modules/directory/hook/use-toggle-featured-article';
import { toast } from 'sonner';

interface ArticleHeroPinValue {
    /** Whether the article is pinned to the directory hero carousel. */
    isPinned: (articleID: number) => boolean;
    /** Undefined for anyone without the admin permission to pin. */
    toggle?: (articleID: number, pin: boolean) => void;
}

const ArticleHeroPinContext = createContext<ArticleHeroPinValue>({
    isPinned: () => false,
});

/**
 * Carries the directory hero's pinned set to every article tile on the page.
 *
 * A context rather than a prop threaded through the grids: the tiles that need
 * it sit several layers below the route, and the toggle has to be one fetcher
 * for the page rather than one per tile.
 */
export const ArticleHeroPinProvider = ({
    pinnedArticleIDs,
    children,
}: {
    pinnedArticleIDs: number[];
    children: ReactNode;
}) => {
    // What this session has changed since the page loaded, over the loader's
    // list. The articles leaf declines to revalidate on a form action, so the
    // loader's list is fixed for the life of the page: without this the label
    // would still read "Pin" on an article just pinned, and the only way to
    // unpin it would be a reload.
    const [changed, setChanged] = useState<Record<number, boolean>>({});

    const { toggleFeaturedArticle } = useToggleFeaturedArticle(
        (error) => {
            if (error.Message) {
                toast.error(error.Message);
            }
        },
        // Only on success, so a refused pin — over the cap, say — leaves the
        // label describing what is actually stored.
        (articleID, pinned) => {
            setChanged((current) => ({ ...current, [articleID]: pinned }));
            toast.success(
                pinned
                    ? 'Pinned to the directory hero. Reload to see the carousel change.'
                    : 'Unpinned from the directory hero.',
            );
        },
    );

    const loaded = useMemo(() => new Set(pinnedArticleIDs), [pinnedArticleIDs]);

    const isPinned = useCallback(
        (articleID: number) => changed[articleID] ?? loaded.has(articleID),
        [changed, loaded],
    );

    const value = useMemo(
        () => ({ isPinned, toggle: toggleFeaturedArticle }),
        [isPinned, toggleFeaturedArticle],
    );

    return (
        <ArticleHeroPinContext.Provider value={value}>
            {children}
        </ArticleHeroPinContext.Provider>
    );
};

export const useArticleHeroPin = () => useContext(ArticleHeroPinContext);
